import { createContext } from '@fluentui/react-context-selector';
import type {
  AttributeFilters,
  FilterPreset,
  Filters,
  ReportGroupData,
  SnapshotId,
  TableColumnSort,
  TableColumnVisibility,
  TableEndpoint,
  TableRequestBody,
  Timezone,
} from '@ipfabric/utils';
import type { LazyLoadRowsHook } from 'components/LazyRowContainer/useLazyLoadRows';
import type { RuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import type { SimpleFilterField } from 'components/Table/components/SimpleFilter/types/SimpleFilterField';
import type {
  IntentFilters,
  TableActionsItem,
  TableModel,
  TableReports,
  TableRowIdValue,
  TableSelectionState,
} from 'components/Table/model/TableModel';
import type { TableRowMeta } from 'components/Table/model/TableRowMeta';
import { array } from 'fp-ts';
import type React from 'react';
import type { AppTranslations } from 'translations/translations';
import type {
  CommandModel,
  CommandModelCommandContext,
  CommandModelCommandDispatcher,
  CommandModelCommandFactory,
  CommandModelMergedState,
} from 'utils/hooks/useCommandModel';
import type { NotificationsHook } from 'utils/hooks/useNotifications';
import {
  type ContextSelectorsHook,
  useContextSelectors,
} from 'utils/react/useContextSelectors';
import type { ApiHook } from 'utils/request/useApi';
import type { SparseArray } from 'utils/types/SparseArray';

/**
 * Disable select-ability of some rows
 */
export type RowSelectionFilter = {
  /**
   * Pass through to Table
   */
  canRowBeSelected?: (row: unknown, state?: TableSelectionState) => boolean;
  /**
   * Pass through to Table
   */
  disableSelectAll?: boolean;
  /**
   * Table filter must also be provided
   */
  selectAllFilter?: Filters;
};

/**
 * Props passed from ApiTable/useApiTable to outer state
 *
 * Pass through all props here in ApiTable.tsx and useApiTable.tsx to the outer state.
 */
export interface ApiTableOuterStateProps {
  /**
   * Extra data passed to some special feature tables
   *
   * Used only on several tables that support it.
   */
  bindVariables?: Record<string, unknown>;
  enableIntentEditor?: boolean;
  enableTableReports?: boolean;
  /**
   * If not provided, defaults to `endpoint.id`
   */
  id?: string;
  /**
   * Merge custom record into loaded table rows by row ids
   */
  dataPatch?: ApiTableDataPatch;
  /**
   * Explicitly render loading indicator instead of content
   *
   * Useful when an external operation is running, e.g. deleting a row.
   */
  loading?: boolean;
  /**
   * Patch every single row after loading
   *
   * Useful e.g. for adding severities for coloring cells.
   *
   * Arg typed as any - casting function argument would be tricky in the userland
   * and there is no meaningful typing of unknown row anyway.
   */
  patchRow?: (row: any) => Record<string, unknown> & TableRowMeta;
  /**
   * Custom content for other api documentation / related endpoints
   */
  renderCustomApiDoc?: () => React.ReactNode;
  /**
   * Key of data row under which a unique identifier can be found.
   *
   * Value under this key should be of {@link TableRowIdValue} type.
   * You don't have to use database id, but any unique identifier.
   * For example for a table of devices, device serial number can be used
   * if you need to work with it for bulk operations.
   */
  rowIdField?: string;
  rowSelectionFilter?: RowSelectionFilter;
  /**
   * Textual description of what the table is about
   */
  tableDescription?: React.ReactNode;
  disableTableRowDetail?: boolean;
  tableRowDetailContainer?: HTMLElement | null;
  menuItems?: TableActionsItem[];
}

export interface ApiTableColumnTypeDoc {
  type: string;
  markdownContent: string;
}

export type SubmitFiltersEvent = (filters: Filters) => void;

/**
 * Determines which operation has set current state.filters
 *
 * "empty" = filters were not set yet or set by some "reset to empty" operation
 */
export type FiltersOrigin = 'simple' | 'advanced' | 'preset' | 'empty';

export type ApiTableDataPatch<
  TShape extends Record<string, unknown> = Record<string, unknown>,
> = Record<TableRowIdValue, TShape>;

export interface ApiTableState {
  /** Data are lazily loaded according to scrolling position so there may be holes. */
  data: SparseArray<unknown>;
  lastRequestPayload: TableRequestBody<any> | null;
  /**
   * Column visibility applied on last data load
   *
   * This is useful to remember, so we don't have to reload data when user configures column visibility:
   * - when a column is hidden
   * - when a column is revealed back, but no rows were loaded in the meantime, so we still don't have to reload
   */
  lastLoadColumnVisibility: TableColumnVisibility | undefined;
  rowCount: number | null;
  /** @see TableOuterStateProps.rowCountMore */
  rowCountMore: boolean;
  sort: TableColumnSort | undefined;
  advancedFilter: {
    open: boolean;
    /**
     * Form valid state of advanced filter component (empty if form is invalid).
     * Output-only from AdvancedFilter, the filter component does not react on changes
     * to this field.
     */
    filters: Filters | undefined;
    /**
     * Input-only - used to set UI state of advanced filter from outside by identity change
     * of this value.
     */
    externalFilters: Filters | undefined;
  };
  description: {
    open: boolean;
  };
  intentVerifications: {
    addRule: boolean;
  };
  reports: TableReports;
  groups: ReportGroupData[];
  intentFilters: IntentFilters;
  // Form state of simple filters
  simpleFilterFields: Record<string, SimpleFilterField>;
  // Last state of submitted simple filter fields; applicable when filtersOrigin is 'simple'.
  simpleFilterFieldsSubmitted: Record<string, SimpleFilterField>;
  // Filters to be sent in table request
  filters: Filters;
  filtersOrigin: FiltersOrigin;
  selectedFilterPreset: null | FilterPreset;
}

export interface ApiTableAdditionalCommandContext {
  api: ApiHook;
  getTableModel: () => TableModel;
  getLazyLoadRows: () => LazyLoadRowsHook;
  msg: AppTranslations;
  notifications: NotificationsHook;
  onFiltersSubmit?: SubmitFiltersEvent;
}

export interface ApiTableOuterState extends ApiTableOuterStateProps {
  /**
   * Table definition
   *
   * Intentionally not part of ApiTableOuterStateProps because we need to type it `<unknown>`
   * inside, otherwise Typescript errors appear when passing it to Table<unknown> component,
   * and `<any>` from outside, otherwise ApiTable and useApiTable do not accept any actual value.
   */
  endpoint: TableEndpoint<any>;
  snapshotId: SnapshotId | null;
  initialFilters?: Filters;
  attributeFilters: AttributeFilters;
  staticFilters?: Filters;
  ruleContext: RuleContext;
  timezone: Timezone;
}

type S = ApiTableState;
type C = ApiTableAdditionalCommandContext;
type O = ApiTableOuterState;

export type ApiTableCommandContext = CommandModelCommandContext<S, C, O>;
export type ApiTableCommandFactory<TCommandArgs = never> =
  CommandModelCommandFactory<S, C, O, TCommandArgs>;
export type ApiTableDispatch = CommandModelCommandDispatcher<S, C, O>;
export type ApiTableMergedState = CommandModelMergedState<S, O>;
export type ApiTableModel = CommandModel<S, C, O> & {
  tableModel: TableModel;
};

export const advancedFilterInitialState: ApiTableState['advancedFilter'] = {
  filters: undefined,
  open: false,
  externalFilters: undefined,
};

export const createApiTableInitialState = (
  initialFilters: Filters | undefined,
  initialFilterPreset: FilterPreset | undefined,
  initialSort: TableColumnSort | undefined,
): ApiTableState => ({
  data: array.empty,
  rowCount: null,
  rowCountMore: false,
  advancedFilter: advancedFilterInitialState,
  description: {
    open: false,
  },
  intentVerifications: {
    addRule: false,
  },
  lastRequestPayload: null,
  lastLoadColumnVisibility: undefined,
  reports: {},
  groups: [],
  simpleFilterFields: {},
  simpleFilterFieldsSubmitted: {},
  sort: initialSort,
  filters: initialFilters ?? initialFilterPreset?.filter ?? {},
  filtersOrigin: 'empty',
  selectedFilterPreset: initialFilterPreset ?? null,
  intentFilters: {},
});

export const ApiTableContext = createContext<ApiTableModel>(undefined!);

export const useApiTableContextSelectors: ContextSelectorsHook<
  ApiTableModel
> = (selectors) => useContextSelectors(ApiTableContext, selectors);
